import { useApolloClient } from "@apollo/client/react/hooks"

import { exists } from "shared/lib/exists"
import { useEvent } from "shared/lib/useEvent"
import { DropHandler } from "shared/types/DragAndDrop"

import { useUpdateProductPosition } from "entities/product-positions/api/useUpdateProductPosition"
import { readProductPositionFragment } from "entities/product-positions/lib/readProductPositionFragment"
import {
  UNASSIGNED_PRODUCTS_TAB_ID,
  UNASSIGNED_ROOMS_TAB_ID
} from "entities/project-children/lib/projectTabIDs"
import { useUpdateRoom } from "entities/rooms/api/useUpdateRoom"
import { readRoomCompleteFragment } from "entities/rooms/lib/readRoomFragment"

interface UseProjectChildrenSortingProps {
  projectID: string
  floorID: string | null
}

export function useProjectChildrenSorting({
  projectID,
  floorID
}: UseProjectChildrenSortingProps) {
  const { cache } = useApolloClient()
  const [updateProductPosition] = useUpdateProductPosition()
  const [updateRoom] = useUpdateRoom()

  const move: DropHandler = useEvent(
    (id, prevIndex, prevParentID, nextIndex, nextParentID) => {
      const room = readRoomCompleteFragment(id, cache)
      const productPosition = readProductPositionFragment(id, cache)

      // Determine the correct parent ID
      let resolvedParentID = nextParentID
      if (!resolvedParentID) {
        if (
          !floorID ||
          floorID === UNASSIGNED_PRODUCTS_TAB_ID ||
          floorID === UNASSIGNED_ROOMS_TAB_ID
        ) {
          resolvedParentID = projectID
        } else if (floorID) {
          resolvedParentID = floorID
        }
      }

      const updatePositionArgs = {
        parentID: resolvedParentID,
        projectID
      }

      if (exists(nextIndex)) {
        if (room) {
          return updateRoom({
            ...updatePositionArgs,
            displayPosition: nextIndex,
            room: { id }
          })
        }
        if (productPosition) {
          return updateProductPosition({
            ...updatePositionArgs,
            displayPosition: nextIndex,
            productPosition: { id }
          })
        }
      }

      throw new Error("Moved Item Not Found!")
    }
  )

  const insert = useEvent(
    (
      itemID: string,
      index: number | null | undefined,
      parentID: string | null
    ) => {
      return move(itemID, null, null, index, parentID)
    }
  )

  return { move, insert }
}
